"use client";

import clsx from "clsx";
import React, {useMemo} from "react";
import {type ArtifactData, useArtifactPanel, useAutoOpenArtifact} from "@/contexts/chat/ArtifactPanelContext";
import {computeBinaryArtifactId, getFileExtension} from "@/utils/fileHelpers";
import {detectFileType} from "@/utils/fileTypeDetection";
import {useBlobUrl, useExcelData} from "@/hooks/useFilePreview";
import {useInView} from "@/hooks/useInView";
import FileBubbleFrame from "./ask-file-bubble/FileBubbleFrame";
import FileThumbnail from "./ask-file-bubble/FileThumbnail";

const AskFileBubble: React.FC<{
   isUser?: boolean;
   agentName?: string;
   brandOverride?: string;
   className?: string;
   fileData: {type: "binary"; mimeType: string; data: string; url?: string};
   fileName: string;
   isLastInGroup?: boolean;
   autoOpen?: boolean;
   hideAgentHeader?: boolean;
   feedbackSlot?: React.ReactNode;
   copyText?: string;
}> = ({
   isUser,
   agentName,
   brandOverride,
   className,
   fileData,
   fileName,
   isLastInGroup = true,
   autoOpen = false,
   hideAgentHeader = false,
   feedbackSlot,
   copyText,
}) => {
   const {openArtifact} = useArtifactPanel();
   // Defer base64 decode + heavy thumbnail work until the bubble scrolls into
   // view. For attachment-heavy transcripts this avoids running
   // base64ToBytes / xlsx parse / docx render for every message on mount.
   const [thumbRef, inView] = useInView<HTMLDivElement>("200px");

   const cleanName = fileName.replace("📎 ", "");
   const extension = getFileExtension(cleanName, fileData.mimeType);
   const fileType = detectFileType(fileData.mimeType);
   // Remote-URL files (v2 attachments stored in GCS) carry no base64 — skip
   // the decode entirely; previews fetch `fileData.url` instead.
   const decodeSource = inView && !fileData.url ? fileData.data : undefined;
   const excelData = useExcelData(decodeSource, fileType.isExcel);
   const blobUrl = useBlobUrl(decodeSource, fileData.mimeType);
   const artifactId = useMemo(
      () => computeBinaryArtifactId(cleanName, fileData.data || fileData.url || ""),
      [cleanName, fileData.data, fileData.url]
   );

   // Concatenating the full base64 payload into a data URL can be expensive
   // for large attachments. Skip the allocation entirely until the card is
   // on-screen so off-screen bubbles don't pay the cost.
   const dataUrl = useMemo(
      () => (inView && !fileData.url ? `data:${fileData.mimeType};base64,${fileData.data}` : ""),
      [inView, fileData.mimeType, fileData.data, fileData.url]
   );

   const buildArtifact = (): ArtifactData => ({
      kind: "binary",
      id: artifactId,
      fileName: cleanName,
      mimeType: fileData.mimeType,
      base64: fileData.data,
      url: fileData.url,
   });

   const openInPanel = () => {
      if (!fileData.data && !fileData.url) return;
      openArtifact(buildArtifact());
   };

   useAutoOpenArtifact(
      autoOpen && !!(fileData.data || fileData.url),
      artifactId,
      buildArtifact,
      openArtifact
   );

   return (
      <FileBubbleFrame
         isUser={isUser}
         agentName={agentName}
         brandOverride={brandOverride}
         className={className}
         isLastInGroup={isLastInGroup}
         hideAgentHeader={hideAgentHeader}
         feedbackSlot={feedbackSlot}
         copyText={copyText}
      >
         <button
            type="button"
            onClick={openInPanel}
            className={clsx(
               "group relative block overflow-hidden rounded-xl border border-300",
               "bg-200/50 hover:bg-200 transition-colors cursor-pointer",
               "w-[200px] text-left"
            )}
         >
            <div
               ref={thumbRef}
               className="relative h-[140px] bg-100 flex items-center justify-center overflow-hidden"
            >
               {inView && (
                  <FileThumbnail
                     fileType={fileType}
                     dataUrl={dataUrl}
                     blobUrl={blobUrl}
                     excelData={excelData}
                     base64Data={fileData.data}
                     url={fileData.url}
                     fileName={cleanName}
                     mimeType={fileData.mimeType}
                  />
               )}
            </div>

            <div className="absolute bottom-[38px] left-2">
               <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase bg-black/40 text-white backdrop-blur-sm">
                  {extension}
               </span>
            </div>

            <div className="px-3 py-2.5 border-t border-300">
               <p className="font-medium text-800 text-xs truncate">{cleanName}</p>
            </div>
         </button>
      </FileBubbleFrame>
   );
};

export default AskFileBubble;
